import { defineBlocksWithJsonArray } from 'blockly/core';

import {
  BEGINNER_LED_ACTION_OPTIONS,
  BEGINNER_PRESET_COLOR_OPTIONS,
} from '../constants.js';
import {
  beginnerTimeField,
  brightnessPercentField,
  getBeginnerLedPinOptions,
  getBeginnerRgbPinOptions,
} from '../helpers/block-builders.js';
import { BEGINNER_PIXEL_CUSTOM_COLOUR_EXTENSION } from '../helpers/pixel-colour-extension.js';

const LED_BLOCK_STYLE = 'beginner_led_blocks';

function beginnerColourField(name: string, colour = '#ff0000') {
  return {
    type: 'field_beginner_colour',
    name,
    colour,
  };
}

const standardLedBlockDefinitions = [
  {
    type: 'beginner_std_led_turn',
    message0: 'turn LED %1 on pin %2',
    args0: [
      {
        type: 'field_dropdown',
        name: 'ACTION',
        options: BEGINNER_LED_ACTION_OPTIONS,
      },
      {
        type: 'field_dropdown',
        name: 'PIN',
        options: getBeginnerLedPinOptions,
      },
    ],
    previousStatement: null,
    nextStatement: null,
    style: LED_BLOCK_STYLE,
    tooltip: 'Turns an LED on, off, or toggles it.',
    helpUrl: '',
  },
  {
    type: 'beginner_std_led_blink',
    message0: 'blink LED on pin %1',
    args0: [
      {
        type: 'field_dropdown',
        name: 'PIN',
        options: getBeginnerLedPinOptions,
      },
    ],
    message1: 'ON time %1 OFF time %2',
    args1: [beginnerTimeField('ON_TIME'), beginnerTimeField('OFF_TIME')],
    previousStatement: null,
    nextStatement: null,
    style: LED_BLOCK_STYLE,
    tooltip: 'Blinks an LED with separate on and off times.',
    helpUrl: '',
  },
  {
    type: 'beginner_std_led_brightness',
    message0: 'set LED on pin %1 brightness %2 %',
    args0: [
      {
        type: 'field_dropdown',
        name: 'PIN',
        options: getBeginnerLedPinOptions,
      },
      brightnessPercentField('BRIGHTNESS', 100),
    ],
    previousStatement: null,
    nextStatement: null,
    style: LED_BLOCK_STYLE,
    tooltip: 'Sets how bright an LED shines using a percentage.',
    helpUrl: '',
  },
  {
    type: 'beginner_rgb_configure',
    message0: 'configure RGB LED red pin %1 green pin %2 blue pin %3',
    args0: [
      {
        type: 'field_dropdown',
        name: 'RED_PIN',
        options: getBeginnerRgbPinOptions,
      },
      {
        type: 'field_dropdown',
        name: 'GREEN_PIN',
        options: getBeginnerRgbPinOptions,
      },
      {
        type: 'field_dropdown',
        name: 'BLUE_PIN',
        options: getBeginnerRgbPinOptions,
      },
    ],
    previousStatement: null,
    nextStatement: null,
    style: LED_BLOCK_STYLE,
    tooltip: 'Sets up the pins used by an RGB LED.',
    helpUrl: '',
  },
  {
    type: 'beginner_rgb_set_color',
    message0: 'set RGB LED color %1 %2',
    args0: [
      {
        type: 'field_dropdown',
        name: 'COLOR',
        options: BEGINNER_PRESET_COLOR_OPTIONS,
      },
      beginnerColourField('CUSTOM_COLOUR', '#ff0000'),
    ],
    extensions: [BEGINNER_PIXEL_CUSTOM_COLOUR_EXTENSION],
    previousStatement: null,
    nextStatement: null,
    style: LED_BLOCK_STYLE,
    tooltip: 'Sets the color of an RGB LED using a preset or custom colour.',
    helpUrl: '',
  },
  {
    type: 'beginner_rgb_turn_off',
    message0: 'turn RGB LED off',
    previousStatement: null,
    nextStatement: null,
    style: LED_BLOCK_STYLE,
    tooltip: 'Turns the RGB LED off.',
    helpUrl: '',
  },
  {
    type: 'beginner_rgb_brightness',
    message0: 'set RGB LED brightness %1 %',
    args0: [brightnessPercentField('BRIGHTNESS', 100)],
    previousStatement: null,
    nextStatement: null,
    style: LED_BLOCK_STYLE,
    tooltip: 'Sets how bright the RGB LED shines using a percentage.',
    helpUrl: '',
  },
];

export function registerBeginnerStandardAndRgbLedBlocks(): void {
  defineBlocksWithJsonArray(standardLedBlockDefinitions);
}
